import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, Timer } from 'lucide-react';
import { RESPONSE_TIME_TARGET_MINUTES, type SlaSummary } from '@nbh/shared';
import { StatTile } from '../ui/StatTile';
import { PeriodHeading } from './PeriodHeading';
import { formatDuration, formatPercent } from '../../lib/format';

export function SlaPanel({ sla, baselineAt }: { sla: SlaSummary; baselineAt: string | null }) {
  const { t } = useTranslation();

  // Only judge the target when there is an average to judge — no data is not a miss.
  const missesTarget =
    sla.averageResponseMinutes !== null &&
    sla.averageResponseMinutes > RESPONSE_TIME_TARGET_MINUTES;

  return (
    <section>
      <PeriodHeading label={t('admin.sla.heading')} baselineAt={baselineAt} />
      <div className="grid grid-cols-2 gap-3">
        <StatTile
          label={t('admin.sla.avgResponse')}
          value={formatDuration(sla.averageResponseMinutes)}
          hint={t('admin.sla.target', { minutes: RESPONSE_TIME_TARGET_MINUTES })}
          emphasis={sla.averageResponseMinutes === null ? 'normal' : missesTarget ? 'warn' : 'good'}
          icon={<Clock className="size-4" aria-hidden="true" />}
        />
        <StatTile
          label={t('admin.sla.mttr')}
          value={formatDuration(sla.meanTimeToRepairMinutes)}
          hint={t('admin.sla.mttrHint')}
          icon={<Timer className="size-4" aria-hidden="true" />}
        />
        <StatTile
          label={t('admin.sla.breaches')}
          value={String(sla.ackBreaches)}
          hint={t('admin.sla.breachesHint')}
          emphasis={sla.ackBreaches > 0 ? 'warn' : 'normal'}
          icon={<AlertTriangle className="size-4" aria-hidden="true" />}
        />
        <StatTile
          label={t('admin.sla.resolveBreaches')}
          value={String(sla.resolveBreaches)}
          hint={t('admin.sla.resolveBreachesHint')}
          emphasis={sla.resolveBreaches > 0 ? 'warn' : 'normal'}
          icon={<AlertTriangle className="size-4" aria-hidden="true" />}
        />
        <StatTile
          label={t('admin.sla.proofCoverage')}
          value={formatPercent(sla.proofCoverage)}
          hint={t('admin.sla.proofHint')}
        />
      </div>
    </section>
  );
}
